
import { ref } from 'vue'

import { useNormalizedGraphQLValidationErrors } from '@/composables/useNormalizedGraphQLValidationErrors'
import { useMutation } from '@vue/apollo-composable'
import { useStripper } from "@jinkosoft/stripper"

import cloneDeep from 'lodash/cloneDeep'
import pick from 'lodash/pick'

export function useMutationForm(input = {}) {

    const {
        formValues,
        inputKey,
        resultKey,
        mutationQuery,
        variables = {},
        options = {},
        transformerCallback = null,
    } = input

    const {
        onSaved,
        beforeSubmit,
        afterSubmit,
        onValidationError,
    } = options

    const initialDefaultValues      = cloneDeep(formValues)

    const form                      = ref({ ...initialDefaultValues })
    const inputVariables            = ref(variables)

    const result                    = ref(null)

    const {
        rawErrors,
        errors,
        reset : resetValidationErrors,
        parseValidation,
        isValidationError,
        hasErrors
    } = useNormalizedGraphQLValidationErrors()
    const generalErrors = ref([])

    const { mutate, onDone, onError, loading } = useMutation(mutationQuery)

    const {
        stripValueRecursive,
        stripValuesRecursive,
        stripKeyFromArray,
        stripValueFromArray
    } = useStripper()

    const submit = async (submitOptions = {}) => {

        const {
            mutateOptions   = {},
            updateFormOnSave    = true
        } = submitOptions

        resetErrors()

        try {

            let submitData      = cloneDeep(form.value)

            if (typeof beforeSubmit === 'function') {
                submitData = beforeSubmit(submitData)
            }

            submitData      = stripValueRecursive('__typename', submitData)

            const payload = {
                ...(inputKey ? { [inputKey]: submitData } : submitData),
                ...inputVariables.value,
            }

            result.value = await mutate(payload, mutateOptions)

            if(updateFormOnSave){
                setForm(result.value?.data?.[resultKey])
            }

            if (typeof afterSubmit === 'function') {
                afterSubmit(result.value?.data?.[resultKey])
            }

            if (typeof onSaved === 'function') {
                onSaved(result.value?.data?.[resultKey])
            }

            return result.value

        } catch (error) {

            if(isValidationError(error)){
                parseValidation(error)

                if (typeof onValidationError === 'function') {
                    onValidationError(errors)
                }


            } else {
                generalErrors.value = (error?.graphQLErrors ?? [])
                    .filter((err) => {
                        return !err.extensions?.validation
                            && !err.extensions?.validationErrors
                    })
                    .map((err) => err.message)
                    .filter(Boolean)
            }

            return null
        }
    }

    const resetErrors = () => {
        resetValidationErrors()
        generalErrors.value = []
    }

    const setVariables  = (variables) => {
        inputVariables.value    = variables
    }

    const setForm       = (data) => {

        const clonedData     = structuredClone(stripValueRecursive('__typename', data))

        if (transformerCallback) {
            transformerCallback(clonedData)
        }

        const filtered = pick(clonedData, Object.keys(initialDefaultValues))

        form.value = merge(cloneDeep(initialDefaultValues), filtered)

    }

    return {
        loading,

        setVariables,
        setForm,
        submit,

        form,
        result,

        rawErrors,
        errors,
        generalErrors,
        hasErrors,
        resetErrors,

        stripValueRecursive,
        stripKeyFromArray,
        stripValueFromArray,
    }
}
